// SVG export: a faithful, dependency-free vector export of nodes.
import type { Document } from './document.ts';
import type { NodeData, NodeId, Paint, Rect } from './types.ts';
import { localTransform, nodeOutline, pathToSvg, sideWeights, sideStrokeRing, flattenPath, dashOutline, lineSegment, type Mat, type PathCmd } from './geometry.ts';
import { colorToHex, imageFilterCss, isGradient, blendCss } from './util.ts';
import { layoutNodeText, textPadding, textBox, UNDERLINE_OFFSET, UNDERLINE_THICKNESS, bulletShape, shapedPathData, getTextShaper, type TextLayout } from './text.ts';
import { layoutTextOnPath } from './textpath.ts';
import { connectorGeometry } from './layout.ts';
import { visualBounds, alphaMask } from './render2d.ts';
import { hasDrawStroke, drawStrokeGeometry, polygonsToSvg, seedOf } from './draw.ts';
import { invert, mul } from './geometry.ts';

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const f = (n: number) => +n.toFixed(3);
const mat = (m: Mat) => `matrix(${m.map(f).join(' ')})`;

/** A shader's rendered pixels as a data: URI (the app has them; headless exports don't). */
export type ShaderImages = (nodeId: NodeId, kind: 'fill' | 'effect', index: number) => string | null;

class SvgWriter {
  defs: string[] = [];
  private uid = 0;
  constructor(
    readonly doc: Document,
    /** Text as glyph outlines (exact, needs no fonts) when the shaper has the font. */
    readonly outlineText = true,
    readonly shaderImage: ShaderImages | null = null,
  ) {}
  id(prefix: string) {
    return `${prefix}${++this.uid}`;
  }

  paint(p: Paint, n: NodeData): { value: string; opacity: number } | null {
    if (p.visible === false) return null;
    if (p.type === 'SOLID') return { value: colorToHex(p.color, false), opacity: p.color.a * (p.opacity ?? 1) };
    if (isGradient(p)) {
      // SVG has no conic or diamond gradients: those export as radial
      const id = this.id('g');
      const from = p.from ?? { x: 0.5, y: 0 };
      const to = p.to ?? { x: 0.5, y: 1 };
      const stops = p.stops
        .map((s) => `<stop offset="${f(s.position)}" stop-color="${colorToHex(s.color, false)}" stop-opacity="${f(s.color.a)}"/>`)
        .join('');
      if (p.type === 'GRADIENT_LINEAR')
        this.defs.push(`<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${f(from.x * n.width)}" y1="${f(from.y * n.height)}" x2="${f(to.x * n.width)}" y2="${f(to.y * n.height)}">${stops}</linearGradient>`);
      else {
        const r = Math.hypot((to.x - from.x) * n.width, (to.y - from.y) * n.height);
        this.defs.push(`<radialGradient id="${id}" gradientUnits="userSpaceOnUse" cx="${f(from.x * n.width)}" cy="${f(from.y * n.height)}" r="${f(r)}">${stops}</radialGradient>`);
      }
      return { value: `url(#${id})`, opacity: p.opacity ?? 1 };
    }
    if (p.type === 'PATTERN') return this.pattern(p);
    return null;
  }

  private patternDepth = 0;
  /** Pattern fill: an SVG <pattern> holding the source layer (two rows for hexagonal grids). */
  private pattern(p: Extract<Paint, { type: 'PATTERN' }>): { value: string; opacity: number } | null {
    const src = this.doc.get(p.sourceId);
    if (!src || this.patternDepth > 2) return null;
    const sb = this.doc.worldBounds(src.id);
    const k = p.scale ?? 1;
    const tw = sb.width * k,
      th = sb.height * k;
    if (tw <= 0 || th <= 0) return null;
    const gx = tw + (p.spacing?.x ?? 0),
      gy = th + (p.spacing?.y ?? 0);
    this.patternDepth++;
    const parent = src.parent ? this.doc.worldTransform(src.parent) : [1, 0, 0, 1, 0, 0];
    const one = (dx: number, dy: number) => `<g transform="translate(${f(dx)} ${f(dy)}) scale(${f(k)}) translate(${f(-sb.x)} ${f(-sb.y)})"><g transform="${mat(parent as Mat)}">${this.node(src)}</g></g>`;
    let body: string, w: number, h: number;
    if (p.tile === 'HORIZONTAL_HEXAGONAL') (body = one(0, 0) + one(gx / 2, gy) + one(-gx / 2, gy)), (w = gx), (h = gy * 2);
    else if (p.tile === 'VERTICAL_HEXAGONAL') (body = one(0, 0) + one(gx, gy / 2) + one(gx, -gy / 2)), (w = gx * 2), (h = gy);
    else (body = one(0, 0)), (w = gx), (h = gy);
    this.patternDepth--;
    const id = this.id('pt');
    this.defs.push(`<pattern id="${id}" patternUnits="userSpaceOnUse" width="${f(w)}" height="${f(h)}">${body}</pattern>`);
    return { value: `url(#${id})`, opacity: p.opacity ?? 1 };
  }

  /**
   * Effects as one SVG filter, applied in order (each step reads the previous result). Background
   * blur has no SVG equivalent and is left out; a progressive blur exports as a uniform one.
   */
  effectsFilter(n: NodeData): string {
    const list = n.effects.filter((e) => e.visible !== false && e.type !== 'BACKGROUND_BLUR');
    if (!list.length) return '';
    const id = this.id('fx');
    const parts: string[] = [];
    let cur = 'SourceGraphic';
    list.forEach((e, i) => {
      const r = `r${i}`;
      if (e.type === 'DROP_SHADOW')
        parts.push(`<feDropShadow in="${cur}" dx="${f(e.offset.x)}" dy="${f(e.offset.y)}" stdDeviation="${f(e.radius / 2)}" flood-color="${colorToHex(e.color, false)}" flood-opacity="${f(e.color.a)}" result="${r}"/>`);
      else if (e.type === 'LAYER_BLUR') parts.push(`<feGaussianBlur in="${cur}" stdDeviation="${f((e.blurType === 'PROGRESSIVE' ? (e.radius + (e.startRadius ?? 0)) / 2 : e.radius) / 2)}" result="${r}"/>`);
      else if (e.type === 'INNER_SHADOW')
        parts.push(
          `<feOffset in="SourceAlpha" dx="${f(e.offset.x)}" dy="${f(e.offset.y)}" result="${r}o"/><feGaussianBlur in="${r}o" stdDeviation="${f(e.radius / 2)}" result="${r}b"/>` +
            `<feComposite in="SourceAlpha" in2="${r}b" operator="out" result="${r}i"/><feFlood flood-color="${colorToHex(e.color, false)}" flood-opacity="${f(e.color.a)}"/>` +
            `<feComposite in2="${r}i" operator="in" result="${r}s"/><feMerge result="${r}"><feMergeNode in="${cur}"/><feMergeNode in="${r}s"/></feMerge>`,
        );
      else if (e.type === 'NOISE') {
        const c = e.color ?? { r: 0, g: 0, b: 0, a: 1 };
        const op = e.opacity ?? 0.25,
          dens = e.density ?? 0.5;
        const a = `${f(12 * op)} 0 0 0 ${f(-(1 - dens) * 12 * op)}`;
        const color = e.noiseType === 'MULTITONE' ? `1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 ${a}` : `0 0 0 0 ${f(c.r)} 0 0 0 0 ${f(c.g)} 0 0 0 0 ${f(c.b)} ${a}`;
        parts.push(
          `<feTurbulence type="fractalNoise" baseFrequency="${f(0.5 / Math.max(0.25, e.size ?? 1))}" numOctaves="1" seed="${(e.seed ?? seedOf(n.id, 3)) % 1000}" result="${r}t"/>` +
            `<feColorMatrix in="${r}t" type="matrix" values="${color}" result="${r}c"/><feComposite in="${r}c" in2="SourceAlpha" operator="in" result="${r}n"/>` +
            `<feMerge result="${r}"><feMergeNode in="${cur}"/><feMergeNode in="${r}n"/></feMerge>`,
        );
      } else if (e.type === 'TEXTURE')
        parts.push(
          `<feTurbulence type="fractalNoise" baseFrequency="${f(1 / Math.max(0.5, e.size ?? 4))}" numOctaves="2" seed="${(e.seed ?? seedOf(n.id, 7)) % 1000}" result="${r}t"/>` +
            `<feDisplacementMap in="${cur}" in2="${r}t" scale="${f((e.radius ?? 4) * 2)}" xChannelSelector="R" yChannelSelector="G" result="${r}"/>`,
        );
      else return;
      cur = r;
    });
    if (cur === 'SourceGraphic') return '';
    this.defs.push(`<filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">${parts.join('')}</filter>`);
    return ` filter="url(#${id})"`;
  }

  node(n: NodeData): string {
    if (!n.visible) return '';
    const t = n.type === 'CONNECTOR' ? '' : ` transform="${mat(localTransform(n))}"`;
    const bm = blendCss(n.blendMode);
    const op = (n.opacity < 1 ? ` opacity="${f(n.opacity)}"` : '') + (bm ? ` style="mix-blend-mode: ${bm === 'lighter' ? 'plus-lighter' : bm}"` : '');
    const fx = this.effectsFilter(n);
    const inner: string[] = [];
    const d = nodeOutline(n);
    const pathD = d ? pathToSvg(d) : '';
    const rule = n.windingRule === 'EVENODD' ? ' fill-rule="evenodd"' : '';

    if (n.type === 'CONNECTOR') return this.connector(n, op);
    // shader effects own the layer's box: export what the shader made of it, as an image
    const shaderFx = n.effects.map((e, i) => [e, i] as const).filter(([e]) => e.type === 'SHADER' && e.visible !== false);
    const fxImage = shaderFx.length ? this.shaderImage?.(n.id, 'effect', shaderFx[shaderFx.length - 1][1]) : null;
    if (fxImage) return `<g${t}${op}><image href="${esc(fxImage)}" width="${f(n.width)}" height="${f(n.height)}" preserveAspectRatio="none"/></g>`;
    if (n.type === 'TEXT') return `<g${t}${op}${fx}>${this.text(n, n.fills)}</g>`;
    if (n.type === 'LINE') {
      const { a, b } = lineSegment(n);
      if (hasDrawStroke(n)) return `<g${t}${op}${fx}>${this.drawStrokes(n, [{ c: 'M', x: a.x, y: a.y }, { c: 'L', x: b.x, y: b.y }])}</g>`;
      const s = n.strokes.map((p) => this.paint(p, n)).find(Boolean);
      const cap = n.strokeCap === 'ROUND' ? ' stroke-linecap="round"' : n.strokeCap === 'SQUARE' ? ' stroke-linecap="square"' : '';
      return s ? `<g${t}${op}><line x1="${f(a.x)}" y1="${f(a.y)}" x2="${f(b.x)}" y2="${f(b.y)}" stroke="${s.value}" stroke-opacity="${f(s.opacity)}" stroke-width="${f(n.strokeWeight)}"${cap}/></g>` : '';
    }
    if (n.type !== 'GROUP') {
      for (const p of n.fills) {
        if (p.type === 'SHADER') {
          const img = p.visible === false ? null : this.shaderImage?.(n.id, 'fill', n.fills.indexOf(p));
          if (!img) continue;
          const clip = this.id('c');
          this.defs.push(`<clipPath id="${clip}"><path d="${pathD}"/></clipPath>`);
          inner.push(`<image href="${esc(img)}" width="${f(n.width)}" height="${f(n.height)}" preserveAspectRatio="none" clip-path="url(#${clip})"${(p.opacity ?? 1) < 1 ? ` opacity="${f(p.opacity!)}"` : ''}/>`);
          continue;
        }
        if (p.type === 'IMAGE') {
          const clip = this.id('c');
          this.defs.push(`<clipPath id="${clip}"><path d="${pathD}"/></clipPath>`);
          const par = p.scaleMode === 'FIT' ? 'xMidYMid meet' : p.scaleMode === 'STRETCH' ? 'none' : 'xMidYMid slice';
          inner.push(`<image href="${esc(p.src)}" width="${f(n.width)}" height="${f(n.height)}" preserveAspectRatio="${par}" clip-path="url(#${clip})"${imageFilterCss(p.filters) ? ` style="filter:${imageFilterCss(p.filters)}"` : ''}/>`);
          continue;
        }
        const v = this.paint(p, n);
        if (v) inner.push(`<path d="${pathD}" fill="${v.value}"${v.opacity < 1 ? ` fill-opacity="${f(v.opacity)}"` : ''}${rule}/>`);
      }
    }
    const strokes = n.type !== 'GROUP' ? this.strokes(n, pathD) : '';
    const isContainer = ['FRAME', 'COMPONENT', 'INSTANCE', 'GROUP', 'SECTION'].includes(n.type);
    if (isContainer) {
      // masks mask the siblings above them: by their alpha when it varies, else by their outline
      let kids = '';
      let open = false;
      for (const c of this.doc.children(n.id)) {
        if (c.isMask && c.visible) {
          const mid = this.id('mask');
          if (open) kids += '</g>';
          if (alphaMask(c)) {
            this.defs.push(`<mask id="${mid}" style="mask-type:alpha">${this.node(c)}</mask>`);
            kids += `<g mask="url(#${mid})">`;
          } else {
            const o = nodeOutline(c);
            this.defs.push(`<clipPath id="${mid}"><path d="${o ? pathToSvg(o) : ''}" transform="${mat(localTransform(c))}"/></clipPath>`);
            kids += `<g clip-path="url(#${mid})">`;
          }
          open = true;
          continue;
        }
        kids += this.node(c);
      }
      if (open) kids += '</g>';
      let content = kids;
      if (n.clipsContent && n.type !== 'GROUP' && n.type !== 'SECTION') {
        const clip = this.id('c');
        this.defs.push(`<clipPath id="${clip}"><path d="${pathD}"/></clipPath>`);
        content = `<g clip-path="url(#${clip})">${kids}</g>`;
      }
      const fillsG = fx && inner.length ? `<g${fx}>${inner.join('')}</g>` : inner.join('');
      return `<g${t}${op} data-name="${esc(n.name)}">${fillsG}${content}${strokes}</g>`;
    }
    if (n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') {
      const box = textBox(n);
      inner.push(strokes);
      inner.push(`<g transform="translate(${f(box.x)} ${f(box.y)})">${this.text(n, n.textColor ? [{ type: 'SOLID', color: n.textColor }] : [])}</g>`);
      return `<g${t}${op}${fx}>${inner.join('')}</g>`;
    }
    return `<g${t}${op}${fx}>${inner.join('')}${strokes}</g>`;
  }

  /** Brushed, dynamic and variable-width strokes: their filled outlines (same as the canvas). */
  private drawStrokes(n: NodeData, outline: PathCmd[]): string {
    const geo = drawStrokeGeometry(n, outline, (id) => {
      const src = this.doc.get(id);
      if (!src) return null;
      const inv = invert(this.doc.worldTransform(id));
      const polys: { x: number; y: number }[][] = [];
      this.doc.walk(id, (c) => {
        const o = c.visible && c.type !== 'GROUP' && c.type !== 'TEXT' ? nodeOutline(c) : null;
        if (!o) return;
        const t = mul(inv, this.doc.worldTransform(c.id));
        for (const line of flattenPath(o, 0.25)) polys.push(line.points.map((q) => ({ x: t[0] * q.x + t[2] * q.y + t[4], y: t[1] * q.x + t[3] * q.y + t[5] })));
      });
      return polys;
    });
    const d = polygonsToSvg(geo.polygons);
    return n.strokes
      .map((p) => this.paint(p, n))
      .filter((v) => !!v)
      .map((v) => `<path d="${d}" fill="${v!.value}"${v!.opacity < 1 ? ` fill-opacity="${f(v!.opacity)}"` : ''}${geo.rule === 'evenodd' ? ' fill-rule="evenodd"' : ''}/>`)
      .join('');
  }

  strokes(n: NodeData, d: string): string {
    if (sideWeights(n)) {
      const ring = pathToSvg(sideStrokeRing(n));
      return n.strokes
        .map((p) => this.paint(p, n))
        .filter((v) => !!v)
        .map((v) => `<path d="${ring}" fill="${v!.value}" fill-opacity="${f(v!.opacity)}" fill-rule="evenodd"/>`)
        .join('');
    }
    if (!n.strokes.length || n.strokeWeight <= 0) return '';
    if (hasDrawStroke(n)) return this.drawStrokes(n, nodeOutline(n) ?? []);
    const out: string[] = [];
    // closed outlines: the dashes as Figma lays them out (see dashOutline), as plain open paths
    const laid = n.strokeDash?.length ? dashOutline(nodeOutline(n) ?? [], n.strokeDash) : null;
    const sd = laid ? laid.map((l) => 'M' + l.map((q) => `${f(q.x)} ${f(q.y)}`).join(' L')).join(' ') : d;
    // ends and corners (SVG's defaults, butt and miter, are Figma's too)
    const ends = `${n.strokeCap === 'ROUND' ? ' stroke-linecap="round"' : n.strokeCap === 'SQUARE' ? ' stroke-linecap="square"' : ''}${n.strokeJoin === 'ROUND' ? ' stroke-linejoin="round"' : n.strokeJoin === 'BEVEL' ? ' stroke-linejoin="bevel"' : ''}`;
    for (const p of n.strokes) {
      const v = this.paint(p, n);
      if (!v) continue;
      const dash = n.strokeDash?.length && !laid ? ` stroke-dasharray="${n.strokeDash.join(' ')}"` : '';
      if (n.strokeAlign === 'INSIDE' || n.strokeAlign === 'OUTSIDE') {
        const id = this.id('s');
        if (n.strokeAlign === 'INSIDE') this.defs.push(`<clipPath id="${id}"><path d="${d}"/></clipPath>`);
        else this.defs.push(`<mask id="${id}"><rect x="-1e5" y="-1e5" width="2e5" height="2e5" fill="#fff"/><path d="${d}" fill="#000"/></mask>`);
        const attr = n.strokeAlign === 'INSIDE' ? `clip-path="url(#${id})"` : `mask="url(#${id})"`;
        out.push(`<path d="${sd}" fill="none" stroke="${v.value}" stroke-opacity="${f(v.opacity)}" stroke-width="${f(n.strokeWeight * 2)}"${dash}${ends} ${attr}/>`);
      } else out.push(`<path d="${sd}" fill="none" stroke="${v.value}" stroke-opacity="${f(v.opacity)}" stroke-width="${f(n.strokeWeight)}"${dash}${ends}/>`);
    }
    return out.join('');
  }

  text(n: NodeData, fills: Paint[]): string {
    const layout = layoutNodeText(n);
    const fill = fills.map((p) => this.paint(p, n)).find(Boolean);
    if (!fill) return '';
    if (this.outlineText && getTextShaper()) {
      const outlined = this.outlinedText(n, layout, fill);
      if (outlined !== null) return outlined;
    }
    const s = layout.style;
    // OpenType features export as real CSS feature settings
    const feats = [s.caps === 'ALL_SMALL_CAPS' ? 'font-variant-caps: all-small-caps' : s.caps === 'SMALL_CAPS' ? 'font-variant-caps: small-caps' : '', s.features.tnum ? 'font-variant-numeric: tabular-nums' : '', s.features.liga === false ? 'font-variant-ligatures: none' : '', s.featureCss ? `font-feature-settings: ${s.featureCss.replace(/"/g, "'")}` : ''].filter(Boolean).join('; ');
    const attrs = `font-family="${esc(s.fontFamily)}, sans-serif" font-size="${f(s.fontSize)}" font-weight="${s.fontWeight}"${s.italic ? ' font-style="italic"' : ''}${s.letterSpacing ? ` letter-spacing="${f(s.letterSpacing)}"` : ''} fill="${fill.value}"${fill.opacity < 1 ? ` fill-opacity="${f(fill.opacity)}"` : ''}${feats ? ` style="${feats}"` : ''}`;
    if (n.textPath && n.type === 'TEXT') {
      // one <text> per glyph, placed and rotated like the canvas renderer (exact, font-independent)
      const pl = layoutTextOnPath(n);
      return pl.glyphs
        .map((g) => {
          const st = pl.styles[g.style];
          const m = st.font.replace('small-caps ', '').match(/^(italic )?(\d+) ([\d.]+)px (.+), sans-serif$/);
          const font = m ? `font-family="${esc(m[4].replace(/"/g, '').replace(/__ff_\w+/, ''))}, sans-serif" font-size="${m[3]}" font-weight="${m[2]}"${m[1] ? ' font-style="italic"' : ''}` : `font-size="${f(s.fontSize)}"`;
          const paint = st.color ? `fill="${colorToHex(st.color, false)}"` : `fill="${fill.value}"${fill.opacity < 1 ? ` fill-opacity="${f(fill.opacity)}"` : ''}`;
          return `<text ${font} ${paint} transform="translate(${f(g.x)} ${f(g.y)}) rotate(${f((g.angle * 180) / Math.PI)})">${esc(g.text)}</text>`;
        })
        .join('');
    }
    const spans = layout.styles
      ? layout.lines
          .flatMap((l) =>
            (l.pieces ?? []).map((pc) => {
              const st = layout.styles![pc.style];
              const m = st.font.replace('small-caps ', '').match(/^(italic )?(\d+) ([\d.]+)px (.+), sans-serif$/);
              const extra = m ? ` font-weight="${m[2]}" font-size="${m[3]}"${m[1] ? ' font-style="italic"' : ''} font-family="${esc(m[4].replace(/"/g, '').replace(/__ff_\w+/, ''))}, sans-serif"` : '';
              const color = st.color ? ` fill="${colorToHex(st.color, false)}"` : st.link ? ' fill="#0d99ff"' : '';
              const deco = st.decoration !== 'NONE' ? ` text-decoration="${st.decoration === 'UNDERLINE' ? 'underline' : 'line-through'}"` : '';
              return `<tspan x="${f(l.x + pc.x)}" y="${f(l.baseline)}"${extra}${color}${deco}>${esc(pc.text)}</tspan>`;
            }),
          )
          .join('')
      : layout.lines.filter((l) => l.text).map((l) => `<tspan x="${f(l.x)}" y="${f(l.baseline)}">${esc(l.text)}</tspan>`).join('');
    const glyphs: string[] = [];
    const shapes: string[] = [];
    const op = fill.opacity < 1 ? ` fill-opacity="${f(fill.opacity)}"` : '';
    for (const l of layout.lines) {
      if (!l.marker) continue;
      const b = bulletShape(l.marker, l.markerX ?? 0, l.baseline, s.fontSize);
      if (!b) glyphs.push(`<tspan x="${f(l.markerX ?? 0)}" y="${f(l.baseline)}">${esc(l.marker)}</tspan>`);
      else if (b.kind === 'square') shapes.push(`<rect x="${f(b.cx - b.r)}" y="${f(b.cy - b.r)}" width="${f(b.r * 2)}" height="${f(b.r * 2)}" fill="${fill.value}"${op}/>`);
      else if (b.kind === 'circle') shapes.push(`<circle cx="${f(b.cx)}" cy="${f(b.cy)}" r="${f(b.r * 0.85)}" fill="none" stroke="${fill.value}" stroke-width="${f(Math.max(0.75, s.fontSize * 0.06))}"/>`);
      else shapes.push(`<circle cx="${f(b.cx)}" cy="${f(b.cy)}" r="${f(b.r)}" fill="${fill.value}"${op}/>`);
    }
    return `${shapes.join('')}<text ${attrs} xml:space="preserve">${glyphs.join('')}${spans}</text>`;
  }

  /** Text as filled glyph outlines, shaped exactly like the canvas draws it (null: fall back to <text>). */
  private outlinedText(n: NodeData, layout: TextLayout, fill: { value: string; opacity: number }): string | null {
    const s = layout.style;
    const op = fill.opacity < 1 ? ` fill-opacity="${f(fill.opacity)}"` : '';
    const out: string[] = [];
    const rule = (x: number, y: number, w: number, fs: number, deco: string, paint: string) =>
      deco === 'NONE' ? '' : `<rect x="${f(x)}" y="${f(deco === 'UNDERLINE' ? y + fs * UNDERLINE_OFFSET : y - fs * 0.3)}" width="${f(w)}" height="${f(Math.max(1, fs * UNDERLINE_THICKNESS))}" ${paint}/>`;
    if (n.textPath && n.type === 'TEXT') {
      const pl = layoutTextOnPath(n);
      for (const g of pl.glyphs) {
        const st = pl.styles[g.style];
        const d = shapedPathData(g.text, st.font, 0, st.features, 0, 0);
        if (d === null) return null;
        const paint = st.color ? `fill="${colorToHex(st.color, false)}"` : st.link ? 'fill="#0d99ff"' : `fill="${fill.value}"${op}`;
        if (d) out.push(`<path transform="translate(${f(g.x)} ${f(g.y)}) rotate(${f((g.angle * 180) / Math.PI)})" d="${d}" ${paint}/>`);
        out.push(rule(0, 0, g.width, st.fontSize, st.decoration, paint));
      }
      return out.join('');
    }
    for (const l of layout.lines) {
      if (l.marker) {
        const b = bulletShape(l.marker, l.markerX ?? 0, l.baseline, s.fontSize);
        if (!b) {
          const d = shapedPathData(l.marker, layout.font, 0, s.features, l.markerX ?? 0, l.baseline);
          if (d === null) return null;
          out.push(`<path d="${d}" fill="${fill.value}"${op}/>`);
        } else if (b.kind === 'square') out.push(`<rect x="${f(b.cx - b.r)}" y="${f(b.cy - b.r)}" width="${f(b.r * 2)}" height="${f(b.r * 2)}" fill="${fill.value}"${op}/>`);
        else if (b.kind === 'circle') out.push(`<circle cx="${f(b.cx)}" cy="${f(b.cy)}" r="${f(b.r * 0.85)}" fill="none" stroke="${fill.value}" stroke-width="${f(Math.max(0.75, s.fontSize * 0.06))}"/>`);
        else out.push(`<circle cx="${f(b.cx)}" cy="${f(b.cy)}" r="${f(b.r)}" fill="${fill.value}"${op}/>`);
      }
      if (layout.styles) {
        for (const pc of l.pieces ?? []) {
          const st = layout.styles[pc.style];
          const d = shapedPathData(pc.text, st.font, st.letterSpacing, st.features, l.x + pc.x, l.baseline);
          if (d === null) return null;
          const paint = st.color ? `fill="${colorToHex(st.color, false)}"` : st.link ? 'fill="#0d99ff"' : `fill="${fill.value}"${op}`;
          if (d) out.push(`<path d="${d}" ${paint}/>`);
          out.push(rule(l.x + pc.x, l.baseline, pc.width, st.fontSize, st.decoration, paint));
        }
      } else if (l.text) {
        let d: string | null = '';
        for (const [t, x] of l.pieces ? l.pieces.map((p) => [p.text, l.x + p.x] as const) : [[l.text, l.x] as const]) {
          const part = shapedPathData(t, layout.font, s.letterSpacing, s.features, x, l.baseline);
          if (part === null) return null;
          d += part;
        }
        if (d) out.push(`<path d="${d}" fill="${fill.value}"${op}/>`);
        out.push(rule(l.x, l.baseline, l.width, s.fontSize, s.decoration, `fill="${fill.value}"${op}`));
      }
    }
    return out.join('');
  }

  connector(n: NodeData, op: string): string {
    const g = connectorGeometry(this.doc, n);
    const s = n.strokes.map((p) => this.paint(p, n)).find(Boolean) ?? { value: '#1e1e1e', opacity: 1 };
    const pts = g.points;
    const d = g.kind === 'CURVED' ? `M${f(pts[0].x)} ${f(pts[0].y)}C${[pts[1], pts[2], pts[3]].map((p) => `${f(p.x)} ${f(p.y)}`).join(' ')}` : pts.map((p, i) => `${i ? 'L' : 'M'}${f(p.x)} ${f(p.y)}`).join('');
    const id = this.id('m');
    if (n.endCap && n.endCap !== 'NONE') this.defs.push(`<marker id="${id}e" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill="${s.value}"/></marker>`);
    if (n.startCap && n.startCap !== 'NONE') this.defs.push(`<marker id="${id}s" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill="${s.value}"/></marker>`);
    const markers = `${n.endCap && n.endCap !== 'NONE' ? ` marker-end="url(#${id}e)"` : ''}${n.startCap && n.startCap !== 'NONE' ? ` marker-start="url(#${id}s)"` : ''}`;
    // Connector points are page-space; undo the parent transform.
    const parent = this.doc.get(n.parent);
    const pt = parent && parent.type !== 'PAGE' ? ` transform="${mat(invertM(this.doc.worldTransform(parent.id)))}"` : '';
    return `<g${pt}${op}><path d="${d}" fill="none" stroke="${s.value}" stroke-width="${f(n.strokeWeight)}" stroke-linejoin="round"${markers}/></g>`;
  }
}

function invertM(m: Mat): Mat {
  const det = m[0] * m[3] - m[1] * m[2] || 1;
  const id = 1 / det;
  return [m[3] * id, -m[1] * id, -m[2] * id, m[0] * id, (m[2] * m[5] - m[3] * m[4]) * id, (m[1] * m[4] - m[0] * m[5]) * id];
}

/** Export nodes as a standalone SVG (in page coordinates, cropped to their bounds). */
export function toSvg(doc: Document, ids: NodeId[], opts: { padding?: number; background?: string; outlineText?: boolean; shaderImage?: ShaderImages } = {}): string {
  const w = new SvgWriter(doc, opts.outlineText ?? true, opts.shaderImage ?? null);
  let b: Rect | null = null;
  for (const id of ids) {
    const r = visualBounds(doc, doc.must(id));
    b = b ? { x: Math.min(b.x, r.x), y: Math.min(b.y, r.y), width: Math.max(b.x + b.width, r.x + r.width) - Math.min(b.x, r.x), height: Math.max(b.y + b.height, r.y + r.height) - Math.min(b.y, r.y) } : r;
  }
  const pad = opts.padding ?? 0;
  const box = b ?? { x: 0, y: 0, width: 100, height: 100 };
  const body = ids
    .map((id) => {
      const n = doc.must(id);
      const parent = n.parent ? doc.worldTransform(n.parent) : null;
      const inner = w.node(n);
      return parent && doc.get(n.parent)?.type !== 'PAGE' ? `<g transform="${mat(parent)}">${inner}</g>` : inner;
    })
    .join('');
  const bg = opts.background ? `<rect x="${f(box.x - pad)}" y="${f(box.y - pad)}" width="${f(box.width + pad * 2)}" height="${f(box.height + pad * 2)}" fill="${opts.background}"/>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${f(box.width + pad * 2)}" height="${f(box.height + pad * 2)}" viewBox="${f(box.x - pad)} ${f(box.y - pad)} ${f(box.width + pad * 2)} ${f(box.height + pad * 2)}"><defs>${w.defs.join('')}</defs>${bg}${body}</svg>`;
}
